import { useMemo } from "react";
import { throwExpression } from "../utils/util";

/**
 * Describes a single filter, including its possible option values and a predicate function.
 */
export interface FilterDef<TOptionValue, TData> {
  options: TOptionValue[] | readonly TOptionValue[];
  predicate: (row: TData, value: TOptionValue) => boolean;
  facetPredicate?: (row: TData, value: TOptionValue) => boolean;
}

/**
 * Map of filter keys to their filter definitions.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type FilterDefsMap<TData> = Record<string, FilterDef<any, TData>>;

/**
 * Infers the shape of selected filter values from the filter definitions map.
 * Each key is either a single option value or an array of option values.
 */
export type SelectedFiltersFromDefs<TDefs extends FilterDefsMap<TData>, TData = unknown> = {
  [K in keyof TDefs]: TDefs[K]["options"][number] | TDefs[K]["options"];
};

/**
 * Infers the shape of facet counts from the filter definitions map.
 * Each key maps to a record of option string to count.
 */
export type FacetCountsFromDefs<TDefs extends FilterDefsMap<TData>, TData = unknown> = {
  [K in keyof TDefs]: FacetCountsForDef<TData, TDefs[K]>;
};

/**
 * Infers the shape of facet counts for a single filter definition. Returns a map of option values to their counts.
 */
export type FacetCountsForDef<TData, TDef extends FilterDef<unknown, TData>> =
  TDef extends FilterDef<infer TOptionValue, TData> ? Map<TOptionValue, number> : never;

/**
 * The result returned by the facet filter hook, including filtered data and facet counts.
 */
export interface FacetResultFromDefs<TData, TDefs extends FilterDefsMap<TData>> {
  filteredData: TData[];
  facetCounts: FacetCountsFromDefs<TDefs, TData>;
}

export interface UseFacetFilterHookOptions<TData, TDefs extends FilterDefsMap<TData>> {
  filterDefs: TDefs;
  data: TData[];
  selectedFilterValues: SelectedFiltersFromDefs<TDefs, TData>;
}

/**
 * React hook for computing filtered data and facet counts based on filter definitions and selected filters.
 */
export function useFacetFilter<TData, TDefs extends FilterDefsMap<TData>>({
  filterDefs,
  data,
  selectedFilterValues,
}: UseFacetFilterHookOptions<TData, TDefs>): FacetResultFromDefs<TData, TDefs> {
  return useMemo(() => {
    // Get the reduced set of data with all filters applied
    const filteredData = applyFiltersToData(data, filterDefs, selectedFilterValues);

    // Get the facet count for each filter option
    const selectedFilterKeys = Object.keys(selectedFilterValues) as (keyof TDefs)[];
    const facetCounts = Object.fromEntries(
      selectedFilterKeys.map((key) => {
        const def = filterDefs[key] ?? throwExpression(`Filter definition for key "${String(key)}" not found`);
        const counts = new Map(
          def.options.map((option) => {
            // To get the facet count for a specific option, we apply all filters except the current one
            // and then count how many rows match the current option
            // This ensures we get the count of rows that match the current option after applying all other filters
            const subset = applyFiltersToData(data, filterDefs, selectedFilterValues, key);
            const subsetFilteredByOptionValue = applySingleFilter(subset, def, option, true);
            return [option, subsetFilteredByOptionValue.length];
          }),
        );
        return [key, counts];
      }),
    ) as FacetCountsFromDefs<TDefs, TData>;

    return { filteredData, facetCounts };
  }, [filterDefs, data, selectedFilterValues]);
}

/**
 * Applies all the selected filters to the data set using the provided filter definitions.
 */
function applyFiltersToData<TData, TDefs extends FilterDefsMap<TData>>(
  data: TData[],
  filterDefs: TDefs,
  selectedFilterValues: SelectedFiltersFromDefs<TDefs, TData>,

  /** An optional filter to exclude from the filtering process. */
  excludeKey?: keyof TDefs,
): TData[] {
  const selectedFilterKeys = Object.keys(selectedFilterValues) as (keyof TDefs)[];
  const filtersToApply = selectedFilterKeys.filter((key) => key !== excludeKey);
  return filtersToApply.reduce((filteredData, key) => {
    const def = filterDefs[key] ?? throwExpression(`Filter definition for key "${String(key)}" not found`);
    return applySingleFilter(filteredData, def, selectedFilterValues[key], false);
  }, data);
}

/** Apply a single filter to a set of data for a given selected filter value. */
function applySingleFilter<TData, TOptionValue>(
  data: TData[],
  filterDef: FilterDef<TOptionValue, TData>,
  selectedFilterValue: TOptionValue | TOptionValue[],

  /** True if this filter is being applied to calculate facet counts, otherwise false. */
  facet: boolean,
): TData[] {
  // Use the facet predicate if available, otherwise use the regular predicate
  const predicate = facet && filterDef.facetPredicate ? filterDef.facetPredicate : filterDef.predicate;

  return data.filter((row) => {
    // Apply filter to an array of filter values (any match)
    if (Array.isArray(selectedFilterValue)) {
      return selectedFilterValue.length === 0 || selectedFilterValue.some((v) => predicate(row, v));
    }

    // Apply filter to a single filter value
    return predicate(row, selectedFilterValue);
  });
}
